import React from "react";
import { isMobile } from "react-device-detect";
import { animated, interpolate, useSpring } from "react-spring";
import { useGesture } from "react-use-gesture";
import { UseGestureEvent } from "react-use-gesture/dist/types";
import styled from "styled-components";

import IntroCartouche from "../components/map/IntroCartouche";
import Marker from "../components/map/Marker";
import ZoomButtons from "../components/map/ZoomButtons";
import { LoadingContext } from "../contexts/LoadingContent";
import { PinContext } from "../contexts/PinContext";
import { useCluster } from "../hooks/useCluster";
import { usePOI } from "../hooks/usePOI";
import {
  constrainX,
  constrainY,
  constrainZ,
  getPercentZoomed,
  isInBounds,
  round,
  zoomInOnPoint,
} from "../utils/map-utils";
import { mapConfig } from "../utils/map-utils/map-config";
import TileMap from "./TileMap";

const InteractiveMap: React.FC = () => {
  const [pinsMode] = React.useContext(PinContext);
  const { points, getPoints } = usePOI();
  const [loadingState, setLoading] = React.useContext(LoadingContext);

  const gestureTarget = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!points) {
      getPoints();
    }
  }, [getPoints, points]);
  // Setup User submissions
  const {
    clusters: storyClusters,
    makeClusters: makeStoryClusters,
  } = useCluster(points.storyPoints);
  // Setup Heritage Sites
  const { clusters: siteClusters, makeClusters: makeSiteClusters } = useCluster(
    points.sitePoints
  );

  // Set up spring and initial values
  const [{ x, y, scale }, set] = useSpring(() => ({
    x: -1000 / (1 / mapConfig.zoom.default),
    y: 2700 / (1 / mapConfig.zoom.default),
    scale: mapConfig.zoom.default,
    config: { mass: 1, tension: 180, friction: 22 },
  }));

  // on mount
  React.useEffect(() => {
    makeStoryClusters(mapConfig.zoom.default);
    makeSiteClusters(mapConfig.zoom.default);
  }, [makeStoryClusters, makeSiteClusters]);

  // Set breakpoints at different zoom levels
  const setBreakpoints = React.useCallback(
    (newScale) => {
      makeStoryClusters(newScale);
      makeSiteClusters(newScale);
    },
    [makeStoryClusters, makeSiteClusters]
  );

  const zoom = React.useCallback(
    (zoomInBy: number, zoomToX?: number, zoomToY?: number) => {
      const windowW = window.innerWidth;
      const windowH = window.innerHeight;

      // Get current center coords
      const currentCenterX = x.getValue();
      const currentCenterY = y.getValue();

      const screenX = zoomToX || windowW / 2;
      const screenY = zoomToY || windowH / 2;

      const currentScale = scale.getValue();
      const desiredScale = currentScale + zoomInBy;
      const scaleFactor = desiredScale / currentScale;

      const { offsetY, offsetX, newScale } = zoomInOnPoint(
        currentScale,
        scaleFactor,
        screenX,
        screenY,
        currentCenterX,
        currentCenterY
      );

      setBreakpoints(newScale);

      set({
        scale: constrainZ(newScale),
        x: constrainX(zoomToX ? currentCenterX - offsetX : 100, newScale),
        y: constrainY(zoomToY ? currentCenterY - offsetY : 100, newScale),
      });
    },
    [scale, set, setBreakpoints, x, y]
  );

  // Set up gestures to bind to gestureTarget
  const bind = useGesture(
    {
      onDrag: ({
        movement,
        memo = [x.getValue(), y.getValue(), scale.getValue()],
        touches,
      }) => {
        if (
          (!isMobile || touches === 1) &&
          movement[0] !== 0 &&
          movement[1] !== 0
        ) {
          set({
            x: constrainX(memo[0] + movement[0], memo[2]),
            y: constrainY(memo[1] + movement[1], memo[2]),
          });
          return memo;
        }
      },
      onWheel: ({
        event,
        movement: [, a],
        memo = [x.getValue(), y.getValue(), scale.getValue()],
      }) => {
        // @ts-ignore - this does type it correctly.
        const wheelEvent = event as UseGestureEvent<WheelEvent> | null;

        if (wheelEvent) {
          const originX = wheelEvent.clientX;
          const originY = wheelEvent.clientY;

          // Get current center coords
          const currentCenterX = memo[0];
          const currentCenterY = memo[1];

          const oldScale = memo[2];

          // Throttle the amount we scale by. a = vertical wheel delta
          const scaleChanged = Math.pow(0.995, a);

          // Zoom in on the point
          const { offsetY, offsetX, newScale } = zoomInOnPoint(
            oldScale,
            scaleChanged,
            originX,
            originY,
            currentCenterX,
            currentCenterY
          );

          // If the next dimensions are in bounds...
          if (offsetY && offsetX && newScale && isInBounds(newScale)) {
            // Set breakpoints if we hit them
            setBreakpoints(newScale);
            // Set zoom and offset so we can zoom in/out on a point
            set({
              scale: constrainZ(newScale),
              x: constrainX(currentCenterX - offsetX, newScale),
              y: constrainY(currentCenterY - offsetY, newScale),
            });
          }
        }
        return memo;
      },
      onPinch: ({
        movement: [d],
        memo = [x.getValue(), y.getValue(), scale.getValue()],
        origin,
      }) => {
        if (!origin) {
          return null;
        }

        // Set touch origin coordinates
        const originX = origin[0];
        const originY = origin[1];

        // Get current center coords
        const currentCenterX = memo[0];
        const currentCenterY = memo[1];

        const oldScale = memo[2];

        // Throttle the amount we scale by. d = touch delta (reversed)
        const scaleChanged = Math.pow(0.995, -d);

        // Zoom in on the point
        const { offsetY, offsetX, newScale } = zoomInOnPoint(
          oldScale,
          scaleChanged,
          originX,
          originY,
          currentCenterX,
          currentCenterY
        );

        // If the next dimensions are in bounds...
        if (offsetY && offsetX && newScale && isInBounds(newScale)) {
          // Set new breakpoints if we hit them
          setBreakpoints(newScale);
          // Set zoom and offset so we can zoom in/out on a point
          set({
            scale: constrainZ(newScale),
            x: constrainX(currentCenterX - offsetX, newScale),
            y: constrainY(currentCenterY - offsetY, newScale),
          });
        }

        return memo;
      },
    },
    {
      domTarget: gestureTarget,
      event: { passive: false },
    }
  );

  // Bind to gestureTarget ref
  React.useEffect(bind, [bind]);

  return (
    <Wrapper>
      <AnimatedMap
        ref={gestureTarget}
        style={{
          transform: interpolate(
            [x, y, scale],
            (x, y, scale) =>
              `translate(${round(x)}px,${round(y)}px) scale(${scale}) ${!isMobile ? "translateZ(0px)" : ""
              }`
          ),
        }}
      >
        <MapLayer
          style={{
            opacity: interpolate([scale], (scale) =>
              getPercentZoomed(scale, 0.8, 0.5)
            ),
          }}
        >
          <TileMap type={"6000/outer/outer"} />
        </MapLayer>
        <MapLayer
          style={{
            opacity: interpolate([scale], (scale) =>
              getPercentZoomed(scale, mapConfig.zoom.min, 1)
            ),
          }}
        >
          <TileMap type={"6000/inner/inner"} />
        </MapLayer>
        <>
          {/* Following clusters are mapped separately to keep different content types separate */}
          {/* {pinsMode.story &&
            storyClusters.map((cluster, i) => {
              return (
                <Marker
                  key={i}
                  {...cluster}
                  contentType="story"
                  pinType={cluster.type}
                  title={cluster?.data?.title}
                  currentZoom={scale}
                  zoom={zoom}
                />
              );
            })} */}
          {/* {pinsMode.event &&
            eventClusters.map((cluster, i) => {
              return (
                <Marker
                  key={i}
                  {...cluster}
                  contentType="event"
                  pinType={cluster.type}
                  title={cluster?.data?.title}
                  currentZoom={scale}
                  zoom={zoom}
                />
              );
            })} */}
          {pinsMode.site &&
            siteClusters.map((cluster, i) => {
              return (
                <Marker
                  key={i}
                  {...cluster}
                  contentType="site"
                  pinType={cluster.type}
                  title={cluster?.data?.title}
                  currentZoom={scale}
                  zoom={zoom}
                />
              );
            })}
        </>
        <IntroCartouche zoom={zoom} scale={scale} />
      </AnimatedMap>
      <ZoomButtons onZoom={(amount) => zoom(amount)} currentScale={scale} />
      <PreLoad>
        <img
          alt="background"
          src="/boxes/6000/background.jpg"
          onLoad={() => setLoading({ ...loadingState, backgroundLoaded: true })}
        />
      </PreLoad>
    </Wrapper>
  );
};
const PreLoad = styled.div`
  display: none;
`;

const AnimatedMap = styled(animated.div as any)`
  transform-origin: 0 0;
  position: relative;
  :before {
    content: "";
    background: url("/boxes/6000/background.jpg");
    width: ${mapConfig.width}px;
    height: ${mapConfig.height}px;
    position: absolute;
    transform: translate(-50%, -50%);
  }
`;

const MapLayer = styled(animated.div as any)`
  mix-blend-mode: multiply;
`;

const Wrapper = styled.div`
  position: relative;
`;

export default InteractiveMap;
